/**
 * Fila de KPIs del panel global de Estadísticas avanzadas (presentacional).
 *
 * Réplica de la rejilla de KPIs del SSR (`advanced_stats_with_charts.html`,
 * `loadKPIs`): órdenes y entregadas del período, ingresos del período, efectividad,
 * pendientes actuales y anuladas. Cada KPI deriva de su fuente:
 *  - órdenes/entregadas/ingresos del período: del desglose diario (`useDailyOrders`)
 *    agregado al período elegido (`periodTotals`, misma lógica que el SSR).
 *  - efectividad: de `useEffectiveness`.
 *  - pendientes/anuladas actuales: del detalle de timing (`useOrdersTiming`),
 *    contando por estado (igual que el SSR).
 *
 * Sin lógica de datos remota: recibe las queries ya en marcha y muestra un valor de
 * carga por KPI mientras su fuente no esté lista (no bloquea toda la fila si una
 * métrica tarda o falla). La sede activa viaja por `X-Sede-Id` (lib/api).
 */
import { Card, CardContent } from "@/components/ui";

import { formatMoney, periodGlobalLabel, periodTotals, statusCounts } from "../format";
import type { OrdersPeriod } from "../schemas";
import type {
  DailyOrdersStats,
  EffectivenessStats,
  OrdersTimingStats,
} from "../types";

/** Estado mínimo de una query que el KPI necesita para derivar su valor. */
interface QueryLike<TData> {
  data: TData | undefined;
  isSuccess: boolean;
}

interface StatsKpisProps {
  period: OrdersPeriod;
  dailyOrders: QueryLike<DailyOrdersStats>;
  effectiveness: QueryLike<EffectivenessStats>;
  ordersTiming: QueryLike<OrdersTimingStats>;
}

/** Valor mostrado mientras la fuente de un KPI aún no está disponible. */
const LOADING = "…";

interface KpiProps {
  label: string;
  value: string | number;
  sub?: string | undefined;
}

function Kpi({ label, value, sub }: KpiProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 py-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </span>
        <span className="text-2xl font-bold text-slate-900">{value}</span>
        {sub ? <span className="text-xs text-slate-500">{sub}</span> : null}
      </CardContent>
    </Card>
  );
}

export function StatsKpis({
  period,
  dailyOrders,
  effectiveness,
  ordersTiming,
}: StatsKpisProps) {
  const periodLabel = periodGlobalLabel(period);

  const totals =
    dailyOrders.isSuccess && dailyOrders.data
      ? periodTotals(
          dailyOrders.data.daily_stats,
          dailyOrders.data.today_colombia,
          period,
        )
      : null;

  const counts =
    ordersTiming.isSuccess && ordersTiming.data
      ? statusCounts(ordersTiming.data.timing_data)
      : null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Kpi
        label="Órdenes"
        value={totals ? totals.orders : LOADING}
        sub={periodLabel}
      />
      <Kpi
        label="Entregadas"
        value={totals ? totals.delivered : LOADING}
        sub={periodLabel}
      />
      <Kpi
        label="Ingresos"
        value={totals ? formatMoney(totals.revenue) : LOADING}
        sub={periodLabel}
      />
      <Kpi
        label="Efectividad"
        value={
          effectiveness.isSuccess && effectiveness.data
            ? `${effectiveness.data.effectiveness_percentage}%`
            : LOADING
        }
        sub="a tiempo"
      />
      <Kpi
        label="Pendientes"
        value={counts ? counts.pending : LOADING}
        sub="en curso"
      />
      <Kpi
        label="Anuladas"
        value={counts ? counts.cancelled : LOADING}
        sub="total"
      />
    </div>
  );
}
